<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">LINE 推播統計</h1>
      </div>
      <div class="header-actions">
        <span v-if="activeTab === 'broadcast' && syncing" class="sync-status syncing">
          <i class="ri-loader-4-line spin"></i> 正在同步 Insight...
        </span>
        <span v-else-if="activeTab === 'broadcast' && lastSyncedAt" class="sync-status">
          <i class="ri-check-line"></i>
          上次同步：{{ formatDateTime(lastSyncedAt) }}
          <button class="btn-link" @click="handleSync(false)">立即同步</button>
        </span>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>開始日期</label>
        <input v-model="filters.startDate" type="date" class="form-control" />
      </div>
      <div class="filter-group">
        <label>結束日期</label>
        <input v-model="filters.endDate" type="date" class="form-control" />
      </div>
      <div v-if="activeTab !== 'chatdesk'" class="filter-group">
        <label>活動名稱</label>
        <input
          v-model="filters.search"
          type="text"
          class="form-control"
          placeholder="搜尋活動名稱"
        />
      </div>
      <button class="btn btn-primary" @click="fetchData">查詢</button>
    </div>

    <div class="detail-tabs">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'broadcast' }"
        @click="activeTab = 'broadcast'"
      >
        LINE 推播管理
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'workflow' }"
        @click="activeTab = 'workflow'"
      >
        行銷活動流程推播
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'chatdesk' }"
        @click="activeTab = 'chatdesk'"
      >
        ChatDesk 客服訊息
      </button>
    </div>

    <div v-if="activeTab === 'broadcast' && !lastSyncedAt && !syncing" class="insight-notice insight-notice--warn">
      <i class="ri-error-warning-line"></i>
      <span>尚未同步 LINE Insight 資料，開信率與點擊率將顯示為「-」。系統將自動嘗試同步。</span>
    </div>
    <div v-if="activeTab === 'broadcast'" class="insight-notice">
      <i class="ri-information-line"></i>
      <span>
        已讀率與點擊率資料來自 LINE Insight API（僅支援 Verified/Premium 帳號），
        資料有 T+1 延遲（UTC+9 隔天才有資料）。
      </span>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchData">重試</button>
    </div>

    <div v-else class="dashboard-content">
      <section v-if="activeTab === 'broadcast'" class="report-section">
        <div class="section-header">
          <h2 class="section-title">LINE 推播管理</h2>
          <button class="btn btn-primary" @click="handleExport('broadcast')">
            <i class="ri-download-line"></i>
            匯出 Excel
          </button>
        </div>
        <div class="stats-cards">
          <div class="stat-card">
            <div class="stat-label">推播活動數</div>
            <div class="stat-value">{{ summary.totalBroadcasts }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">總發送數</div>
            <div class="stat-value">{{ summary.totalSent.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">總送達數</div>
            <div class="stat-value">{{ summary.totalDelivered.toLocaleString() }}</div>
            <div class="stat-sub" v-if="summary.totalSent > 0">
              送達率 {{ ((summary.totalDelivered / summary.totalSent) * 100).toFixed(1) }}%
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-label">總開信數</div>
            <div class="stat-value">{{ summary.totalOpened.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">整體開信率</div>
            <div class="stat-value">
              {{ summary.overallOpenRate != null ? (summary.overallOpenRate * 100).toFixed(2) + '%' : '-' }}
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-label">整體點擊率</div>
            <div class="stat-value">
              {{ summary.overallClickRate != null ? (summary.overallClickRate * 100).toFixed(2) + '%' : '-' }}
            </div>
          </div>
        </div>

        <div class="table-section">
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>活動名稱</th>
                  <th>訊息類型</th>
                  <th style="text-align:right">排程時間</th>
                  <th style="text-align:right">目標人數</th>
                  <th style="text-align:right">實際發送</th>
                  <th style="text-align:right">送達數</th>
                  <th style="text-align:right">開信數</th>
                  <th style="text-align:right">點擊數</th>
                  <th style="text-align:right">開信率</th>
                  <th style="text-align:right">點擊率</th>
                  <th style="text-align:center">狀態</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredBroadcasts.length === 0">
                  <td colspan="11" class="empty-state">沒有找到資料</td>
                </tr>
                <tr v-for="b in filteredBroadcasts" :key="b.id">
                  <td class="campaign-name">{{ b.campaignName }}</td>
                  <td>
                    <span
                      v-for="t in b.messageTypes"
                      :key="t"
                      class="type-badge"
                    >{{ t }}</span>
                  </td>
                  <td style="text-align:right;white-space:nowrap">{{ formatDateTime(b.scheduledAt) }}</td>
                  <td style="text-align:right">{{ b.targetCount.toLocaleString() }}</td>
                  <td style="text-align:right">{{ b.actualSent.toLocaleString() }}</td>
                  <td style="text-align:right">
                    <span v-if="b.hasInsight">{{ (b.delivered ?? 0).toLocaleString() }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td style="text-align:right">
                    <span v-if="b.hasInsight">{{ (b.opened ?? 0).toLocaleString() }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td style="text-align:right">
                    <span v-if="b.hasInsight">{{ (b.clicked ?? 0).toLocaleString() }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td style="text-align:right">
                    <span v-if="b.openRate != null" :class="getRateClass(b.openRate)">
                      {{ (b.openRate * 100).toFixed(2) }}%
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td style="text-align:right">
                    <span v-if="b.clickRate != null" :class="getRateClass(b.clickRate)">
                      {{ (b.clickRate * 100).toFixed(2) }}%
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td style="text-align:center">
                    <span :class="statusClass(b.status)">{{ statusLabel(b.status) }}</span>
                    <span v-if="!b.hasInsight && b.status === 'completed'" class="insight-pending" title="Insight 資料尚未同步">
                      <i class="ri-time-line"></i>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section v-else-if="activeTab === 'workflow'" class="report-section">
        <div class="section-header">
          <h2 class="section-title">行銷活動流程推播</h2>
          <button class="btn btn-primary" @click="handleExport('workflow')">
            <i class="ri-download-line"></i>
            匯出 Excel
          </button>
        </div>
        <p class="section-note">此區塊為流程推播執行結果（成功／失敗／略過），不含 LINE Insight 開信率與點擊率。</p>
        <div class="stats-cards">
          <div class="stat-card">
            <div class="stat-label">流程活動數</div>
            <div class="stat-value">{{ workflowSummary.totalCampaigns.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">推播執行總數</div>
            <div class="stat-value">{{ workflowSummary.totalActions.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">成功數</div>
            <div class="stat-value">{{ workflowSummary.totalSuccess.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">失敗數</div>
            <div class="stat-value">{{ workflowSummary.totalFailed.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">成功率</div>
            <div class="stat-value">
              {{ workflowSummary.successRate != null ? (workflowSummary.successRate * 100).toFixed(2) + '%' : '-' }}
            </div>
          </div>
        </div>
        <div class="table-section">
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>流程活動名稱</th>
                  <th>活動代碼</th>
                  <th style="text-align:right">最後推播時間</th>
                  <th style="text-align:right">成功</th>
                  <th style="text-align:right">失敗</th>
                  <th style="text-align:right">略過</th>
                  <th style="text-align:right">待處理</th>
                  <th style="text-align:right">成功率</th>
                  <th style="text-align:center">活動狀態</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredWorkflowCampaigns.length === 0">
                  <td colspan="9" class="empty-state">沒有找到資料</td>
                </tr>
                <tr v-for="w in filteredWorkflowCampaigns" :key="w.campaignId">
                  <td class="campaign-name">{{ w.campaignName }}</td>
                  <td>{{ w.campaignCode || '-' }}</td>
                  <td style="text-align:right;white-space:nowrap">{{ formatDateTime(w.lastExecutedAt) }}</td>
                  <td style="text-align:right">{{ w.successCount.toLocaleString() }}</td>
                  <td style="text-align:right">{{ w.failedCount.toLocaleString() }}</td>
                  <td style="text-align:right">{{ w.skippedCount.toLocaleString() }}</td>
                  <td style="text-align:right">{{ w.pendingCount.toLocaleString() }}</td>
                  <td style="text-align:right">
                    {{ w.successRate != null ? (w.successRate * 100).toFixed(2) + '%' : '-' }}
                  </td>
                  <td style="text-align:center">
                    <span :class="campaignStatusClass(w.campaignStatus)">
                      {{ campaignStatusLabel(w.campaignStatus) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section v-else class="report-section">
        <div class="section-header">
          <h2 class="section-title">ChatDesk 客服訊息</h2>
        </div>
        <p class="section-note">
          統計客服透過 ChatDesk 成功送至 LINE 的訊息；Reply 與 Push 分開計算，不包含 AI 回覆及發送失敗的訊息。
        </p>
        <div class="stats-cards">
          <div class="stat-card">
            <div class="stat-label">總發送數</div>
            <div class="stat-value">{{ chatdeskSummary.totalSent.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Reply 發送</div>
            <div class="stat-value">{{ chatdeskSummary.replyCount.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Push 發送</div>
            <div class="stat-value">{{ chatdeskSummary.pushCount.toLocaleString() }}</div>
            <div v-if="chatdeskSummary.fallbackPushCount > 0" class="stat-sub">
              含 Reply 失敗轉 Push {{ chatdeskSummary.fallbackPushCount.toLocaleString() }} 筆
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-label">訊息範本</div>
            <div class="stat-value">{{ chatdeskSummary.templateCount.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">圖片／檔案</div>
            <div class="stat-value">
              {{ (chatdeskSummary.imageCount + chatdeskSummary.fileCount).toLocaleString() }}
            </div>
          </div>
        </div>
        <div class="table-section">
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>日期</th>
                  <th style="text-align:right">總發送</th>
                  <th style="text-align:right">Reply</th>
                  <th style="text-align:right">Push</th>
                  <th style="text-align:right">文字</th>
                  <th style="text-align:right">圖片</th>
                  <th style="text-align:right">檔案</th>
                  <th style="text-align:right">訊息範本</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="chatdeskDaily.length === 0">
                  <td colspan="8" class="empty-state">沒有找到資料</td>
                </tr>
                <tr v-for="item in chatdeskDaily" :key="item.date">
                  <td>{{ item.date }}</td>
                  <td style="text-align:right">{{ item.totalSent.toLocaleString() }}</td>
                  <td style="text-align:right">{{ item.replyCount.toLocaleString() }}</td>
                  <td style="text-align:right">{{ item.pushCount.toLocaleString() }}</td>
                  <td style="text-align:right">{{ item.textCount.toLocaleString() }}</td>
                  <td style="text-align:right">{{ item.imageCount.toLocaleString() }}</td>
                  <td style="text-align:right">{{ item.fileCount.toLocaleString() }}</td>
                  <td style="text-align:right">{{ item.templateCount.toLocaleString() }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { reportsService } from '@/api/modules/reports.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'LineStats',
  setup() {
    const loading = ref(false);
    const syncing = ref(false);
    const error = ref(null);
    const activeTab = ref('broadcast');
    const broadcasts = ref([]);
    const workflowCampaigns = ref([]);
    const summary = ref({
      totalBroadcasts: 0, totalSent: 0, totalDelivered: 0,
      totalOpened: 0, totalClicked: 0, overallOpenRate: null, overallClickRate: null
    });
    const workflowSummary = ref({
      totalCampaigns: 0,
      totalActions: 0,
      totalSuccess: 0,
      totalFailed: 0,
      totalSkipped: 0,
      totalPending: 0,
      successRate: null
    });
    const chatdeskDaily = ref([]);
    const chatdeskSummary = ref({
      totalSent: 0,
      replyCount: 0,
      pushCount: 0,
      fallbackPushCount: 0,
      textCount: 0,
      imageCount: 0,
      fileCount: 0,
      templateCount: 0
    });
    const lastSyncedAt = ref(null);
    const filters = ref({ startDate: '', endDate: '', search: '' });

    const filteredBroadcasts = computed(() => {
      const kw = filters.value.search.trim().toLowerCase();
      if (!kw) return broadcasts.value;
      return broadcasts.value.filter(b =>
        b.campaignName?.toLowerCase().includes(kw)
      );
    });
    const filteredWorkflowCampaigns = computed(() => {
      const kw = filters.value.search.trim().toLowerCase();
      if (!kw) return workflowCampaigns.value;
      return workflowCampaigns.value.filter(w =>
        w.campaignName?.toLowerCase().includes(kw) ||
        w.campaignCode?.toLowerCase().includes(kw)
      );
    });

    const buildParams = () => {
      const params = {};
      if (filters.value.startDate?.trim()) params.startDate = filters.value.startDate;
      if (filters.value.endDate?.trim()) params.endDate = filters.value.endDate;
      return params;
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;
        const response = await reportsService.getLineStats(buildParams());
        if (response.success) {
          broadcasts.value = response.data.broadcasts || [];
          summary.value = response.data.summary || {};
          workflowCampaigns.value = response.data.workflow?.campaigns || [];
          workflowSummary.value = response.data.workflow?.summary || {
            totalCampaigns: 0,
            totalActions: 0,
            totalSuccess: 0,
            totalFailed: 0,
            totalSkipped: 0,
            totalPending: 0,
            successRate: null
          };
          chatdeskDaily.value = response.data.chatdesk?.daily || [];
          chatdeskSummary.value = response.data.chatdesk?.summary || {
            totalSent: 0,
            replyCount: 0,
            pushCount: 0,
            fallbackPushCount: 0,
            textCount: 0,
            imageCount: 0,
            fileCount: 0,
            templateCount: 0
          };
          lastSyncedAt.value = response.data.lastSyncedAt || null;
        } else {
          error.value = response.message || '取得資料失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得資料失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSync = async (silent = true) => {
      try {
        syncing.value = true;
        const response = await reportsService.syncLineInsight({});
        if (response.success) {
          if (!silent) {
            const { synced, skipped, errors, total } = response.data;
            showSuccess(
              '同步完成',
              `共處理 ${total} 筆推播：成功 ${synced} 筆、跳過 ${skipped} 筆、失敗 ${errors} 筆`
            );
          }
          await fetchData();
        }
      } catch (err) {
        if (!silent) {
          showError('同步失敗', err.response?.data?.message || err.message || '同步失敗');
        }
      } finally {
        syncing.value = false;
      }
    };

    const shouldAutoSync = (syncTime) => {
      if (!syncTime) return true;
      const diffHours = (Date.now() - new Date(syncTime).getTime()) / (1000 * 60 * 60);
      return diffHours >= 24;
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const y = date.getFullYear();
      const M = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const m = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${M}-${d} ${h}:${m}`;
    };

    const getRateClass = (rate) => {
      if (rate == null) return 'text-muted';
      if (rate >= 0.3) return 'badge badge-success';
      if (rate >= 0.1) return 'badge badge-warning';
      return 'badge badge-danger';
    };

    const statusLabel = (s) => ({ completed: '完成', sending: '發送中', failed: '失敗' }[s] || s);
    const statusClass = (s) => ({
      completed: 'status-badge status-success',
      sending:   'status-badge status-info',
      failed:    'status-badge status-danger'
    }[s] || 'status-badge');
    const campaignStatusLabel = (s) => ({
      draft: '草稿',
      active: '啟用中',
      paused: '已暫停',
      completed: '已完成',
      archived: '已封存'
    }[s] || (s || '-'));
    const campaignStatusClass = (s) => ({
      draft: 'status-badge',
      active: 'status-badge status-success',
      paused: 'status-badge status-warning',
      completed: 'status-badge status-info',
      archived: 'status-badge status-muted'
    }[s] || 'status-badge');
    const handleExport = async (tab = activeTab.value) => {
      try {
        loading.value = true;
        const params = { ...buildParams(), reportType: 'line-stats', format: 'excel' };
        const blob = await reportsService.exportReport(params);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const tabSuffix = tab === 'workflow' ? '流程推播' : '推播管理';
        link.href = url;
        link.download = `LINE推播統計_${tabSuffix}_${filters.value.startDate || 'all'}_${filters.value.endDate || 'all'}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSuccess('成功', '報表已匯出');
      } catch (err) {
        showError('匯出失敗', err.response?.data?.message || err.message);
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      await fetchData();
      if (shouldAutoSync(lastSyncedAt.value)) {
        handleSync(true);
      }
    });

    return {
      loading, syncing, error, activeTab, broadcasts, summary, lastSyncedAt,
      workflowCampaigns, workflowSummary,
      chatdeskDaily, chatdeskSummary,
      filters, filteredBroadcasts, filteredWorkflowCampaigns,
      fetchData, handleSync, handleExport,
      getRateClass, statusLabel, statusClass,
      campaignStatusLabel, campaignStatusClass,
      formatDateTime
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.sync-status {
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sync-status.syncing { color: #007bff; }

.btn-link {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
  text-decoration: underline;
}

.insight-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  background: #e8f4fd;
  border: 1px solid #b3d9f7;
  border-radius: 6px;
  font-size: 13px;
  color: #1a5f8a;
  margin-bottom: 8px;
  line-height: 1.5;
}

.insight-notice--warn {
  background: #fff8e1;
  border-color: #ffc107;
  color: #856404;
}

.insight-notice i { font-size: 16px; flex-shrink: 0; margin-top: 1px; }

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.detail-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}

.tab-btn.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
  font-weight: 600;
}

.report-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section-title {
  margin: 0;
  font-size: 18px;
  color: #2f3542;
}

.section-note {
  margin: 0;
  font-size: 13px;
  color: #666;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 18px 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,.1);
}

.stat-label { font-size: 13px; color: #666; margin-bottom: 6px; }
.stat-value { font-size: 26px; font-weight: 600; color: #333; }
.stat-sub   { font-size: 12px; color: #888; margin-top: 4px; }

.table-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,.1);
}

.campaign-name {
  font-weight: 500;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.type-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 10px;
  background: #e9ecef;
  color: #495057;
  font-size: 12px;
  margin-right: 3px;
  white-space: nowrap;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.status-success { background: #d1e7dd; color: #0f5132; }
.status-info    { background: #cff4fc; color: #055160; }
.status-danger  { background: #f8d7da; color: #842029; }
.status-warning { background: #fff3cd; color: #664d03; }
.status-muted   { background: #f1f3f5; color: #6c757d; }

.insight-pending {
  margin-left: 4px;
  color: #aaa;
  font-size: 13px;
  vertical-align: middle;
}

.text-muted  { color: #aaa; }

.spin {
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
</style>
